import { Backdrop, CircularProgress, Box, Grid, Typography, TextField } from '@mui/material';
import { SubmitButton } from './SubmitButton';
import { PageName } from '../index';
import { useRef, useState } from 'react';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { fetchAuthSession } from 'aws-amplify/auth';
import { resendSignUpCode } from 'aws-amplify/auth';
import { executePost, executePut } from '@/client/rest';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';

export function InputTokenFormForTransfer(props: any) {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  let tokenFormRef = useRef<HTMLInputElement>(null);
  const { handleConfirmSignUp } = useCognitoAuth();

  const transferPatientRecord = async (accessToken: string) => {
    // どのusernameと紐付けるかを確認するため
    // Cognitoオーソライザーのためにトークンを取得
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();

    try {
      const bodyResult = (await executePost({
        apiName: 'medirecoWebApi',
        path: '/transfer-patient-record',
        options: {
          headers: {
            'Content-Type': 'application/json',
            Authorization: String(token)
          },
          queryParams: {
            accessToken: accessToken
          }
        }
      })) as any;

      const pre_sub = bodyResult.preSub;

      // .net移行処理
      await executePut({
        apiName: 'notification',
        path: `/updateCognitoIdInDotNet?prev_user_id=${pre_sub}`,
        options: {
          headers: {
            Authorization: String(token)
          }
        }
      });
      props.setActivePage(PageName.AfterRegister);
    } catch (error) {
      // LINEユーザがそもそも移行前にサインアップしていない場合や、
      // アクセストークンが無効だった場合、
      // 通常の新規登録と同じ分岐に進ませる
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      props.setActivePage(PageName.AfterRegister);
      return;
    }
  };

  const handleTokenSubmit = async () => {
    const token = tokenFormRef.current!.value.trim();
    if (token !== '') {
      setOpen(true);
      // サインアップ処理
      try {
        await handleConfirmSignUp(String(props.username), token);
      } catch (e) {
        setErrorMessage('ご入力いただいた認証コードが異なります。再度ご確認ください。');
        setOpen(false);
        return;
      }

      // 移行処理
      await transferPatientRecord(props.accessToken); // 成功すれば登録完了ページに遷移する
    }
  };

  const ErrorMessage = (props: { message: string | undefined }) => {
    if (!props.message) return null;
    return (
      <Typography color="error" style={{ fontSize: '12px', marginLeft: '4px' }}>
        {props.message}
      </Typography>
    );
  };

  const handleResendSignUpCode = async () => {
    setOpen(true);
    try {
      await resendSignUpCode({ username: props.username });
    } catch (error) {
      if (typeof error === 'object' && error != null && 'name' in error) {
        switch (error.name) {
          case 'LimitExceededException':
            setErrorMessage('問題が発生しました。少し時間をおいて再度お試しください。');
            break;
          default:
            setErrorMessage('問題が発生しました。再度確認して入力してください。');
        }
      } else {
        setErrorMessage('問題が発生しました。再度確認して入力してください。');
      }
    } finally {
      setOpen(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} spacing={2}>
          <Grid item xs={0} md={3} sm={2} />
          <Grid item xs={12} md={6} sm={8}>
            <Grid container direction={'column'} spacing={2}>
              <Grid item xs={12} mt={2} sx={{ textAlign: 'center', maxWidth: '100% !important' }}>
                <Typography mb={2} pt={2} fontSize={'16px'} className="font-bold">
                  2段階認証
                </Typography>
                <Typography mb={2} pt={2} fontSize={'14px'}>
                  認証コードをメールでお送りしました
                  <br />
                  メールに記載されている認証コードを
                  <br />
                  入力してください
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'left', maxWidth: '100% !important' }}>
                <Typography m={0.5} fontSize={'14px'} className="font-bold">
                  認証コード
                </Typography>
                <TextField
                  inputRef={tokenFormRef}
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: '#fff' }}
                  onChange={(e) => {
                    if (e.currentTarget.value.trim() === '') {
                      setIsSubmitDisabled(true);
                    } else if (isSubmitDisabled) {
                      setIsSubmitDisabled(false);
                    }
                  }}
                />
                <SubmitButton disabled={isSubmitDisabled} label={'送信する'} onClick={handleTokenSubmit} />
                {errorMessage && <ErrorMessage message={errorMessage} />}
                <Grid item xs={12} mt={2} sx={{ textAlign: 'center', maxWidth: '100% !important' }}>
                  <button
                    onClick={() => handleResendSignUpCode()}
                    style={{ color: '#1976d2', borderBottom: '1px solid', fontSize: '12px' }}
                  >
                    認証コードの再送はこちら
                  </button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
